/**
 * US5 (`GET /imports`, `catalogue-admin.md`). The batch history behind the wizard (`List.tsx`) and
 * the diff (`Diff.tsx`) — every past validate, whatever it was committed, abandoned or left
 * untouched, newest first, each row linking to the same `/imports/:batchId` diff screen a fresh
 * upload redirects to. Same `PagedTable` list pattern every other server-paged list here uses
 * (`components/table/data-table.tsx`); `kind` is the one filter `GET /imports` takes, wired as the
 * status dropdown exactly the way `invoicing/List.tsx` wires its own `state` filter, with a
 * `pageSize: 1` count per kind alongside the page read (never derived — the API's own totals).
 */
import { Link, useLoaderData, useOutletContext, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { PiUploadDuotone as UploadIcon } from "react-icons/pi";
import { Button } from "rizzui";
import { EmptyState, ErrorSurface } from "@steelyard/theme";
import { PagedTable, type StatusOption } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { importHistoryColumns } from "./history-columns";
import { listImportBatches, type ImportKind } from "../../lib/services/catalog";
import { hrefWith, readPaging } from "../../lib/paging";
import { can } from "../../lib/permissions";
import type { AccountSummary, PermissionState } from "../../types";

const IMPORT_KINDS: { value: ImportKind; label: string }[] = [
  { value: "catalogue", label: "Catalogue imports" },
  { value: "tier_price", label: "Tier-price imports" },
];

export async function importHistoryLoader({ request }: LoaderFunctionArgs) {
  const params = new URL(request.url).searchParams;
  const { page, pageSize } = readPaging(params);
  const kindParam = params.get("kind");
  const kind = IMPORT_KINDS.some((option) => option.value === kindParam) ? (kindParam as ImportKind) : undefined;

  const [result, ...kindTotals] = await Promise.all([
    listImportBatches({ kind, page, pageSize }),
    ...IMPORT_KINDS.map((option) =>
      listImportBatches({ kind: option.value, page: 1, pageSize: 1 })
        .then((counted) => counted.totalCount)
        .catch(() => null),
    ),
  ]);

  return { result, kindTotals, kind };
}

export default function ImportHistoryPage() {
  const { result, kindTotals, kind } = useLoaderData() as Awaited<ReturnType<typeof importHistoryLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams] = useSearchParams();

  if (!can(permissionState, "catalogue.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="view import history" />;
  }

  const query = (over: Record<string, string | undefined>) => hrefWith("/imports/history", searchParams, over);

  const kindOptions: StatusOption[] = [
    { label: "All kinds", href: query({ kind: undefined, page: undefined }), active: !kind },
    ...IMPORT_KINDS.map((option, index) => ({
      label: option.label,
      href: query({ kind: option.value, page: undefined }),
      active: option.value === kind,
      count: kindTotals[index],
    })),
  ];

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Import History"
        count={result.totalCount}
        description="Every catalogue and tier-price import validated on this account, committed, abandoned or still waiting on a decision."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Data" }, { name: "Import History" }]}
      />

      <PagedTable
        actions={
          // The uploader is `catalogue.administer` (nav.tsx's /imports entry); this list is catalogue.read.
          can(permissionState, "catalogue.administer") ? (
            <Link to="/imports">
              <Button as="span" variant="outline">
                <UploadIcon aria-hidden className="me-1.5 size-4" />
                New import
              </Button>
            </Link>
          ) : undefined
        }
        result={result}
        caption="Import batches, with their outcome counts and current status"
        columns={importHistoryColumns}
        getRowId={(row) => row.batchId}
        status={{ label: "Kind", options: kindOptions }}
        clear={{ active: Boolean(kind), href: query({ kind: undefined, page: undefined }) }}
        empty={
          <EmptyState title="No imports yet">
            Nothing has been uploaded through the catalogue or tier-price importer yet.
          </EmptyState>
        }
      />
    </div>
  );
}
